/**
 * Shared QR-code + OTP-input body rendered during TOTP enrollment.
 *
 * Used by MfaEnrollmentCard and MfaEnrollmentScreen.
 * FAM7 extraction — no behaviour change.
 */
import { Copy, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

interface MfaQrCodeBodyProps {
  qrCode: string;
  secret: string;
  otpCode: string;
  isSubmitting: boolean;
  error: string | null;
  autoFocus?: boolean;
  onOtpChange: (value: string) => void;
  onVerify: (code: string) => void;
  onCopySecret: () => void;
}

/**
 * Renders the QR-code scan + manual-entry secret + OTP input + verify button.
 * Shared between MfaEnrollmentCard (settings page) and MfaEnrollmentScreen (blocking screen).
 */
export function MfaQrCodeBody({
  qrCode,
  secret,
  otpCode,
  isSubmitting,
  error,
  autoFocus = false,
  onOtpChange,
  onVerify,
  onCopySecret,
}: MfaQrCodeBodyProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("mfa.scanQrCode")}</p>
      <div className="flex justify-center">
        <img src={qrCode} alt="TOTP QR Code" className="h-48 w-48 rounded-lg border bg-white p-2" />
      </div>

      {/* Manual secret */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{t("mfa.manualEntry")}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 rounded bg-muted px-3 py-2 text-xs font-mono break-all">
            {secret}
          </code>
          <Button variant="ghost" size="icon" onClick={onCopySecret}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* OTP Input */}
      <div className="space-y-2">
        <p className="text-sm font-medium">{t("mfa.enterCode")}</p>
        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={otpCode}
            onChange={(value) => {
              onOtpChange(value);
              if (value.length === 6) onVerify(value);
            }}
            disabled={isSubmitting}
            autoFocus={autoFocus}
            // No password-manager badge on a centered 6-digit MFA field; also stops
            // input-otp from scheduling its 2s/5s/6s PWM-detection setTimeouts that
            // fire post-jsdom-teardown in unit tests ("window is not defined").
            pushPasswordManagerStrategy="none"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        onClick={() => onVerify(otpCode)}
        disabled={otpCode.length !== 6 || isSubmitting}
        className="w-full"
      >
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t("mfa.verify")}
      </Button>
    </div>
  );
}
